import { ModalProps } from 'antd/es/modal';
import React, { FC, ReactNode, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation } from 'react-query';
import styled from 'styled-components';
import { MemberType, Role, RoleType, Team, UserType } from 'api/models';
import { Form } from 'components/atoms/form';
import { Modal } from 'components/atoms/modal';
import { Option } from 'components/atoms/select/types';
import { toastService } from 'components/molecules/toast';
import {
  PlaceholderForm,
  PlaceholderFormValues,
} from 'components/organisms/placeholders/form';
import { TranslationKey, useTranslate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import {
  CreatePlaceholderPayloadOpts,
  getCreatePlaceholderPayload,
} from 'queries/placeholder';
import { floatUid } from 'utils/uid';

const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding-top: var(--spacing-05) !important;
  }
`;

interface Props extends ModalProps {
  mutationFn?: (form: PlaceholderFormValues) => Promise<Team>;
  roles: Role[];
  placeholders: { [id: number]: Team };
  save: (team: Team) => void;
  cancel: () => void;
  payloadOptions?: CreatePlaceholderPayloadOpts;
  primaryButtonLabel?: string | ReactNode;
  team?: Team;
}

export const PlaceholderCreateOrEditModal: FC<Props> = ({
  placeholders,
  roles,
  save,
  cancel,
  mutationFn,
  payloadOptions,
  primaryButtonLabel,
  team: existingTeam,
  ...rest
}) => {
  const translate = useTranslate();
  const [form] = Form.useForm();
  const _mutationFn = mutationFn || (() => Promise.resolve({} as Team));
  const {
    user: { userType },
  } = useUser();

  const roleOptions: Option[] = useMemo(
    () =>
      (userType === UserType.Partner && existingTeam != null
        ? roles.filter(
            ({ usedByPartners, type }) =>
              type !== RoleType.Vendor || usedByPartners === true,
          )
        : roles
      ).map(({ roleId, roleName, type }) => ({
        label: roleName,
        value: roleId,
        extra: {
          type,
        },
      })),
    [existingTeam, roles, userType],
  );
  const isCustomerRoleAvailable = useMemo(
    () => !!roles.find((x) => x.roleId === -1 || x.type === RoleType.Customer),
    [roles],
  );

  useEffect(() => {
    if (existingTeam != null) {
      const { teamName, roleId } = existingTeam;
      const selectedRoleOption = roleOptions.find(
        (option) => option.value === roleId,
      );
      const selectedRoleType =
        existingTeam.teamType === MemberType.External
          ? RoleType.Customer
          : RoleType.Vendor;

      form.setFieldsValue({
        name: teamName,
        role: selectedRoleOption,
        type: selectedRoleType,
      });
    } else {
      form.setFieldsValue({
        type: RoleType.Vendor,
      });
    }
  }, [form, existingTeam, roleOptions, translate]);

  const { mutate, isLoading } = useMutation(_mutationFn, {
    onSuccess: async (team) => {
      toastService({
        title: translate('placeholder.create.success.title'),
        message: translate('placeholder.create.success.message'),
      }).success();
      save(team);
    },
  });

  const onSave = () => {
    form.validateFields().then((formValues) => {
      if (mutationFn) {
        mutate(formValues);
      } else {
        const team: Team =
          existingTeam != null
            ? {
                ...existingTeam,
                teamName: formValues.name,
              }
            : ({
                teamId: floatUid(),
                ...getCreatePlaceholderPayload(formValues, payloadOptions),
              } as Team);

        save(team);
      }
    });
  };

  return (
    <StyledModal
      zIndex={1031}
      {...rest}
      title={translate(
        (existingTeam != null
          ? 'resource_management.actions.edit_placeholder'
          : 'templates.placeholder.create.modal.title') as TranslationKey,
      )}
      centered
      visible
      okButtonProps={{
        loading: isLoading,
      }}
      onOk={onSave}
      okText={
        primaryButtonLabel ?? (
          <FormattedMessage id="templates.placeholder.create.button" />
        )
      }
      cancelButtonProps={{
        type: 'text',
      }}
      onCancel={cancel}
      cancelText={<FormattedMessage id="cancel" />}
    >
      <PlaceholderForm
        roleOptions={roleOptions}
        form={form}
        placeholders={placeholders}
        isEditMode={existingTeam != null}
        isCustomerRoleAvailable={isCustomerRoleAvailable}
      />
    </StyledModal>
  );
};
